import React from "react";
import { IndianRupee } from "lucide-react";
import { AppButton, FieldError, FormField, Input, SegmentedControl } from "../ui";
import { formatCurrency } from "../../utils/format";
import { priceFeeState, readMoney } from "../../utils/enrolmentFee";

const DISCOUNT_OPTIONS = [
  { value: "amount", label: "₹" },
  { value: "percent", label: "%" },
];

/**
 * CourseFeeFields — the fee ONE student pays for ONE course: the fee before
 * discount, this student's discount (₹ or %) with its reason, and the payable
 * that leaves. The discount is the enrolment's, so nobody else in the course or
 * batch is touched by it.
 *
 * Value-first: `value` is the editor state (`feeFromCourse` /
 * `feeFromEnrolment` in utils/enrolmentFee) and `onChange` gets the next one.
 * The rules are that file's — the server's twin — so the payable shown here is
 * the payable the save stores.
 *
 * Props:
 *   value      — { courseFee, discountMode, discountValue, discountReason }
 *   onChange   — (next) => void
 *   listFee    — the course's own fee (number), null when it names none,
 *                undefined when unknown. Offers "Use ₹X" when the typed fee differs.
 *   stored     — the saved enrolment's { totalFees, paidFees } when editing, so a
 *                discount below what is already paid is shown as the server will
 *                refuse it
 *   courseName — names the controls for a screen reader; the form has several
 *   serverError— the save's refusal for this course, if any
 */
const CourseFeeFields = ({ value, onChange, listFee, stored = null, courseName = "", serverError = "" }) => {
  const discountId = `discount-${React.useId()}`;
  const plan = priceFeeState(value, stored);
  const fieldError = (field) => (plan.field === field ? plan.error.replace(/^This course: /, "") : "");
  const update = (patch) => onChange({ ...value, ...patch });

  const typedFee = readMoney(value.courseFee) ?? 0; // blank is a free course
  const differsFromCourse = typeof listFee === "number" && !(Number.isFinite(typedFee) && Math.abs(typedFee - listFee) < 0.005);
  const hasDiscount = (readMoney(value.discountValue) || 0) !== 0;
  const isPercent = value.discountMode === "percent";
  const of = courseName ? ` for ${courseName}` : "";

  let feeHint = null;
  if (listFee === null) {
    feeHint = "This course has no fee set. Enter this student's fee.";
  } else if (differsFromCourse) {
    feeHint = (
      <span className="tw-inline-flex tw-flex-wrap tw-items-center tw-gap-x-1.5">
        The course's fee is {formatCurrency(listFee)}.
        <AppButton variant="ghost" size="xs" type="button" onClick={() => update({ courseFee: String(listFee) })}>
          Use {formatCurrency(listFee)}
        </AppButton>
      </span>
    );
  } else if (typeof listFee === "number" && listFee > 0) {
    feeHint = "From the course.";
  }

  const discountError = fieldError("discountValue");
  const discountDescribedBy = discountError ? `${discountId}-error` : undefined;

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      <div className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2">
        <FormField label="Course fee" error={fieldError("courseFee")} hint={feeHint}>
          <Input
            inputMode="decimal"
            autoComplete="off"
            leading={<IndianRupee size={14} aria-hidden="true" />}
            value={value.courseFee}
            onChange={(e) => update({ courseFee: e.target.value })}
            placeholder="0"
            aria-label={`Course fee${of}`}
          />
        </FormField>

        <FormField label="Discount" htmlFor={discountId} error={discountError}>
          <div className="tw-flex tw-items-center tw-gap-2">
            <SegmentedControl
              label={`Discount in rupees or percent${of}`}
              size="xs"
              options={DISCOUNT_OPTIONS}
              value={value.discountMode}
              onChange={(mode) => update({ discountMode: mode })}
              className="tw-shrink-0"
            />
            <Input
              id={discountId}
              inputMode="decimal"
              autoComplete="off"
              value={value.discountValue}
              onChange={(e) => update({ discountValue: e.target.value })}
              placeholder="0"
              invalid={!!discountError}
              aria-describedby={discountDescribedBy}
              aria-label={`Discount${isPercent ? " percent" : " in rupees"}${of}`}
              trailing={isPercent ? <span className="tw-text-sm">%</span> : undefined}
              trailingPad="icon"
            />
          </div>
        </FormField>
      </div>

      {hasDiscount && (
        <FormField
          label="Reason for discount"
          error={fieldError("discountReason")}
          hint="Optional. Kept on this student's record — no one else in the course gets this discount."
        >
          <Input
            value={value.discountReason}
            onChange={(e) => update({ discountReason: e.target.value })}
            maxLength={200}
            placeholder="e.g. Sibling, early bird, scholarship"
            aria-label={`Reason for discount${of}`}
          />
        </FormField>
      )}

      {plan.set && (
        <p className="tw-m-0 tw-text-sm tw-text-content-muted" aria-live="polite">
          Payable{" "}
          <span className="tw-font-semibold tw-tabular-nums tw-text-content">{formatCurrency(plan.set.totalFees)}</span>
          {plan.set.discount && (
            <span className="tw-tabular-nums">
              {" "}· {formatCurrency(plan.set.discount.amount)} off {formatCurrency(plan.set.courseFee)}
            </span>
          )}
        </p>
      )}
      {serverError && !plan.error && <FieldError>{serverError}</FieldError>}
    </div>
  );
};

export default CourseFeeFields;
